import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { fetchTransfers, fetchStats, fetchSources, fetchSettings, saveSettings, fetchTokens, COINS, API } from "@/lib/api";
import { beep, browserNotify, describe } from "@/lib/notify";
import axios from "axios";
import Header from "@/components/whale/Header";
import NetFlowStrip from "@/components/whale/NetFlowStrip";
import ChartPanel from "@/components/whale/ChartPanel";
import WhaleFeed from "@/components/whale/WhaleFeed";
import SettingsPanel from "@/components/whale/SettingsPanel";
import SourceStatus from "@/components/whale/SourceStatus";
import AnalystChat from "@/components/whale/AnalystChat";
import TokenManager from "@/components/whale/TokenManager";
import FlowExplorer from "@/components/whale/FlowExplorer";

const usePref = (key, initial) => {
  const [v, setV] = useState(() => JSON.parse(localStorage.getItem(key) ?? JSON.stringify(initial)));
  const set = (x) => { localStorage.setItem(key, JSON.stringify(x)); setV(x); };
  return [v, set];
};

const matches = (t, s) => t.amount_usd >= s.min_usd && s.coins.includes(t.symbol) && s.directions.includes(t.direction);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [tokens, setTokens] = useState([]);
  const loadTokens = useCallback(() => fetchTokens().then(setTokens).catch(() => {}), []);
  useEffect(() => { loadTokens(); }, [loadTokens]);
  const coins = [...COINS, ...tokens.map((t) => t.symbol)];
  const [transfers, setTransfers] = useState([]);
  const [stats, setStats] = useState([]);
  const [sources, setSources] = useState([]);
  const [prices, setPrices] = useState({});
  const [coin, setCoin] = useState("BTC");
  const [market, setMarket] = useState(null);
  const selectCoin = useCallback((sym, m = null) => { setCoin(sym); setMarket(m); }, []);
  const viewCoin = useCallback((sym, m = null) => {
    selectCoin(sym, m);
    document.getElementById("chart-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selectCoin]);
  const [alertCount, setAlertCount] = useState(0);
  const [browserOn, setBrowserOn] = usePref("wf-browser", false);
  const [soundOn, setSoundOn] = usePref("wf-sound", true);
  const seen = useRef(null);
  const seenKey = useRef(null);
  const prefs = useRef({});
  prefs.current = { browserOn, soundOn };

  const minUsd = settings?.min_usd ?? 1_000_000;

  const poll = useCallback(async () => {
    if (!settings) return;
    const [t, s, src, p] = await Promise.all([
      fetchTransfers(minUsd), fetchStats(minUsd), fetchSources(), axios.get(`${API}/prices`).then((r) => r.data),
    ]).catch(() => [null]);
    if (!t) return;
    const key = JSON.stringify([settings.min_usd, settings.coins, settings.directions]);
    if (seen.current && seenKey.current === key) {
      const fresh = t.filter((x) => !seen.current.has(x.id) && matches(x, settings));
      fresh.slice(0, 3).forEach((x) => {
        const { title, body } = describe(x);
        (x.direction === "inflow" ? toast.error : toast.success)(title, { description: body });
        if (prefs.current.soundOn) beep(x.direction);
        if (prefs.current.browserOn) browserNotify(x);
      });
      if (fresh.length) setAlertCount((c) => c + fresh.length);
    }
    seen.current = new Set([...(seen.current || []), ...t.map((x) => x.id)].slice(-2000));
    seenKey.current = key;
    setTransfers(t); setStats(s); setSources(src); setPrices(p);
  }, [settings, minUsd]);

  useEffect(() => { fetchSettings().then(setSettings); }, []);
  useEffect(() => {
    poll();
    const id = setInterval(poll, 10000);
    return () => clearInterval(id);
  }, [poll]);

  const onThreshold = async (v) => setSettings(await saveSettings({ min_usd: v }));

  const onBrowser = async (on) => {
    if (on && "Notification" in window) {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") return toast.error("Browser notifications were blocked");
    }
    setBrowserOn(on);
  };

  return (
    <div className="terminal-bg">
      <Header minUsd={minUsd} onThreshold={onThreshold} prices={prices} alertCount={alertCount} email={user?.email} onLogout={logout} />
      <main className="space-y-4 p-3 sm:p-4 lg:p-6">
        <NetFlowStrip stats={stats} coin={coin} onCoin={selectCoin} />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-8">
            <ChartPanel coin={coin} market={market} onCoin={selectCoin} transfers={transfers} coins={coins} />
            <SourceStatus sources={sources} />
          </div>
          <div className="space-y-4 lg:col-span-4">
            <WhaleFeed transfers={transfers} />
            <TokenManager tokens={tokens} onChange={loadTokens} user={user} />
            <SettingsPanel settings={settings} onSaved={setSettings} browserOn={browserOn} onBrowser={onBrowser} soundOn={soundOn} onSound={setSoundOn} coins={coins} />
          </div>
        </div>
        <FlowExplorer stats={stats} onView={viewCoin} onTracked={loadTokens} />
      </main>
      <AnalystChat />
    </div>
  );
}
